'use client'

import { useEffect } from 'react'

/**
 * Handles stale chunk loading errors that occur after deployments.
 *
 * When a new version is deployed, the old hashed chunk files are replaced.
 * Users with cached pages still reference old chunk URLs, causing
 * "Failed to load chunk" / "Loading chunk failed" errors and downstream
 * React hydration error #419.
 *
 * This component listens for these errors globally and triggers a single
 * full-page reload so the browser fetches the new HTML + chunks.
 */
export function ChunkErrorRecovery() {
  useEffect(() => {
    function isChunkLoadError(error: unknown): boolean {
      if (!(error instanceof Error)) return false
      const msg = error.message
      return (
        msg.includes('Failed to fetch dynamically imported module') ||
        msg.includes('Loading chunk') ||
        msg.includes('Failed to load chunk') ||
        msg.includes('Loading CSS chunk') ||
        // Next.js specific chunk errors
        msg.includes('ChunkLoadError') ||
        (msg.includes('_next/static/chunks') && msg.includes('failed'))
      )
    }

    const RELOAD_KEY = 'chunk_error_reload'

    function handleReload() {
      // Prevent infinite reload loops — only reload once per session
      if (sessionStorage.getItem(RELOAD_KEY)) return
      sessionStorage.setItem(RELOAD_KEY, Date.now().toString())
      window.location.reload()
    }

    function onError(event: ErrorEvent) {
      if (isChunkLoadError(event.error)) {
        event.preventDefault()
        handleReload()
      }
    }

    function onUnhandledRejection(event: PromiseRejectionEvent) {
      if (isChunkLoadError(event.reason)) {
        event.preventDefault()
        handleReload()
      }
    }

    window.addEventListener('error', onError)
    window.addEventListener('unhandledrejection', onUnhandledRejection)

    // Clear the reload flag after a successful page load so future
    // deploys can trigger a fresh reload
    const clearTimer = setTimeout(() => {
      sessionStorage.removeItem(RELOAD_KEY)
    }, 10_000)

    return () => {
      window.removeEventListener('error', onError)
      window.removeEventListener('unhandledrejection', onUnhandledRejection)
      clearTimeout(clearTimer)
    }
  }, [])

  return null
}
